import {defineDefaults} from '#lib/helpers/react';
import React from 'react';

function setQueryString(key, value, replace) {
    const params = new URLSearchParams(window.location.search);
    if (value == undefined) {
        params.delete(key);
    } else {
        const encoded = encodeURIComponent(value);
        params.set(key, encoded);
    }
    const {protocol, host, pathname} = window.location;
    const newurl = `${protocol}//${host}${pathname}?${params.toString()}`;
    if (replace) window.history.replaceState({path: newurl}, '', newurl);
    else window.history.pushState({path: newurl}, '', newurl);
}

function getQueryString(key) {
    if (!key) return;
    const {search} = window.location;
    const searchParams = new URLSearchParams(search);
    const value = searchParams.get(key);
    if (value == undefined) return value;
    return decodeURIComponent(value);
}

const defaultOptions = {
    replace: true
};

const initialOptions = {};

/**
 * Ties React state to query string parameters. It's easier to share and restore app state when it's in the URL.
 * @param {string} key Query string key
 * @param {string} initialValue
 * @param {object=} options Global `useQueryString` options
 * @param {string} options.as Desired value type
 * @param {boolean} options.replace If `true`, it will replace the current {@link https://developer.mozilla.org/en-US/docs/Web/API/History history} entry, other wise it will push a new entry in it. It defaults to `true`.
 * @returns
 */
export function useQueryString(key, initialValue, globalOptions = initialOptions) {
    defineDefaults(globalOptions, defaultOptions);
    const [value, _setValue] = React.useState(() => getQueryString(key) || initialValue);

    const setValue = React.useCallback(
        (newValue, options) => {
            const replace = options?.replace || globalOptions.replace;
            setQueryString(key, newValue, replace);
            _setValue(getQueryString(key));
        },
        [key, globalOptions.replace]
    );

    return [value, setValue];
}
